import { proxy, subscribe } from 'valtio';
import { CACHE_VERSION, clearWorkspaceCache } from './cache';

const IDB_NAME = `nivaas-preferences-v${CACHE_VERSION}`;
const IDB_STORE = 'workspace';
const IDB_KEY = 'selected';

type WorkspaceState = {
  id: string;
  name: string;
  /** Whether this workspace supports occupancy-based pricing */
  supportsOccupancyPricing: boolean;
};

/**
 * Open IndexedDB connection
 */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(IDB_NAME, CACHE_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
  });
}

/**
 * Get workspace from IndexedDB
 */
async function getFromIDB(): Promise<WorkspaceState | null> {
  try {
    if (typeof indexedDB === 'undefined') return null;

    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const request = store.get(IDB_KEY);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result ?? null);

      tx.oncomplete = () => db.close();
    });
  } catch (e) {
    console.warn('[Workspace] Failed to read from IndexedDB:', e);
    return null;
  }
}

/**
 * Save workspace to IndexedDB
 */
async function saveToIDB(workspace: WorkspaceState): Promise<void> {
  try {
    if (typeof indexedDB === 'undefined') return;
    if (!workspace.id) return; // Don't save empty workspace

    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      const request = store.put(workspace, IDB_KEY);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();

      tx.oncomplete = () => db.close();
    });
  } catch (e) {
    console.warn('[Workspace] Failed to save to IndexedDB:', e);
  }
}

/**
 * Current workspace state (reactive via valtio)
 */
export const currentWorkspace = proxy<WorkspaceState>({
  id: '',
  name: '',
  supportsOccupancyPricing: false,
});

/**
 * Initialize workspace from IndexedDB (call once on app load)
 */
export async function initializeWorkspaceFromCache(): Promise<void> {
  const cached = await getFromIDB();
  if (cached?.id) {
    currentWorkspace.id = cached.id;
    currentWorkspace.name = cached.name;
    currentWorkspace.supportsOccupancyPricing = cached.supportsOccupancyPricing ?? false;
  }
}

// Subscribe to changes and persist to IndexedDB
if (typeof window !== 'undefined') {
  // Initialize from cache on load
  initializeWorkspaceFromCache();

  // Track previous workspace to clear its cache on switch
  let previousWorkspaceId = '';

  // Persist changes to IndexedDB and clear old workspace cache
  subscribe(currentWorkspace, () => {
    const newId = currentWorkspace.id;

    // Clear cache for the old workspace when switching
    if (previousWorkspaceId && previousWorkspaceId !== newId) {
      clearWorkspaceCache(previousWorkspaceId);
    }
    previousWorkspaceId = newId;

    saveToIDB({
      id: currentWorkspace.id,
      name: currentWorkspace.name,
      supportsOccupancyPricing: currentWorkspace.supportsOccupancyPricing,
    });
  });
}
